import { StoreId } from "../decorators/StoreIdDecorator";
import { AcceleratorDocStatusCalculator } from "../services/AcceleratorDocStatusCalculator";
import {
  DocumentStatus,
  getDocumentStatuses,
  isDocumentStatus,
} from "../types/DocumentStatuses";
import { TemplateType } from "../types/TemplateType";
import { AcceleratorBatch } from "./AcceleratorBatch";
import { AcceleratorBatchSource } from "./AcceleratorBatchSource";
import { AcceleratorDocStoreKey } from "./AcceleratorDoc";
import { ModelStoreKey } from "./Model";

export { AcceleratorDocMetadata, AcceleratorDocMetadataData };

type AcceleratorDocMetadataData = {
  /* AcceleratorDoc UUID */
  uuid: string;
  /* AcceleratorDoc DOCXXX Id */
  docId: string;
  /* Classified Model Name */
  classifiedModelName: string;
  /* Classified DocType */
  classifiedDocType: string;
  /* AcceleratorDoc doc statuses. */
  statuses: Array<DocumentStatus>;
  /* Count of Pages. */
  pageCount: number;
  /* Source */
  source: AcceleratorBatchSource;
};

class AcceleratorDocMetadata {
  @StoreId
  storeId: AcceleratorDocStoreKey;
  uuid: string;
  batchClassId: string = "";
  wasGeneral: boolean;
  wasGeneralFormat: boolean;
  wasUnknown: boolean;
  /* Classified Model Name */
  classifiedModelName: string;
  /* Classified DocType */
  classifiedDocType: string;
  /* AcceleratorDoc doc statuses. */
  _statuses: Array<DocumentStatus>;
  /* Count of Pages. */
  pageCount: number;
  batchInstanceId: string;
  batchName: string;
  source: AcceleratorBatchSource;
  hasModelChanged: boolean;

  stale?: boolean;

  loadingError: boolean = false;

  // TODO: support the wasGeneral, wasUnknown, isGeneral, isUnknown stuff !!!
  constructor(
    data: AcceleratorDocMetadataData & {
      batchInstanceUuid: string;
      batchInstanceId: string;
      batchName: string;
      source: AcceleratorBatchSource;
    },
  ) {
    this.storeId = {
      batchInstanceUuid: data.batchInstanceUuid,
      docId: data.docId,
    };
    this.uuid = data.uuid;
    this.batchName = data.batchName;
    // this.batchClassId = data.batchClassId;
    this.classifiedModelName = data.classifiedModelName;
    this.classifiedDocType = data.classifiedDocType;
    this.pageCount = data.pageCount;
    this.batchInstanceId = data.batchInstanceId;
    this.source = data.source;
    this.wasUnknown = data.statuses.includes("UNKNOWN");
    this.wasGeneral = data.statuses.includes("GENERAL");
    this.wasGeneralFormat = data.statuses.includes("GENERAL_FORMAT");
    this.statuses = data.statuses;
    this.hasModelChanged = false;
  }

  set statuses(statuses: DocumentStatus[]) {
    // check that statuses contains only valid DocumentStatus, then set
    const keys = getDocumentStatuses();
    const isGeneralOrUnknown =
      this.wasGeneral ||
      this.wasGeneralFormat ||
      this.wasUnknown ||
      statuses.includes("GENERAL") ||
      statuses.includes("GENERAL_FORMAT") ||
      statuses.includes("UNKNOWN");
    const filtered = statuses
      .filter((s) => isDocumentStatus(s))
      .filter((s) => !(isGeneralOrUnknown && s === "INVALID"));
    const sorted = filtered.sort((a, b) => keys.indexOf(a) - keys.indexOf(b));
    this._statuses = Array.from(new Set(sorted).values());
  }
  get statuses(): DocumentStatus[] {
    return this._statuses;
  }

  getModelName(): string {
    return this.classifiedModelName;
  }

  guessModelStoreKey(): ModelStoreKey {
    // serialization order is important, must match Model.storeId's field order
    return {
      name: this.classifiedModelName,
      batchClassIdentifier: this.batchClassId,
      docType: this.classifiedDocType,
    };
  }

  setModelChanged(hasModelChanged: boolean) {
    //console.debug(`Setting AcceleratorDocMetadata ${getObjectStoreIdString(this)} as hasModelChanged=${hasModelChanged + ''}`)
    this.hasModelChanged = hasModelChanged;
  }

  setClassifiedModelName(newName: string) {
    this.classifiedModelName = newName;
  }

  /*
   * Used to ensure AcceleratorDocMetadata info is correct before AcceleratorDoc and Model have been hydrated.
   */
  updateDataFromBatchAndModelInfo(
    batch: AcceleratorBatch,
    batchClassId: string,
  ): AcceleratorDocMetadata {
    this.source = batch.source;
    const unknown =
      this.classifiedModelName === "" || this.classifiedModelName === "Unknown";
    if (this.classifiedModelName === "Unknown") {
      this.classifiedModelName = "";
    }

    const isTemplateType: TemplateType = !unknown
      ? "NORMAL"
      : this.statuses.includes("GENERAL")
        ? "GENERAL"
        : this.statuses.includes("GENERAL_FORMAT")
          ? "FORMAT"
          : "UNKNOWN";

    const wasTemplateType: TemplateType = this.wasGeneral
      ? "GENERAL"
      : this.wasGeneralFormat
        ? "FORMAT"
        : this.wasUnknown
          ? "UNKNOWN"
          : "NORMAL";

    const isGeneralOrUnknown =
      isTemplateType === "GENERAL" ||
      isTemplateType === "FORMAT" ||
      isTemplateType === "UNKNOWN" ||
      wasTemplateType === "GENERAL" ||
      wasTemplateType === "FORMAT" ||
      wasTemplateType === "UNKNOWN" ||
      unknown;

    this.statuses = AcceleratorDocStatusCalculator.calculateDocumentStatus_lazy(
      {
        isTemplateType: isTemplateType,
        unknown: this.statuses.includes("UNKNOWN"),
        wasTemplateType: wasTemplateType,
        hasMultiple: this.statuses.includes("MULTIPLE"),
        hasError: this.statuses.includes("ERROR"),
        hasEmpty: this.statuses.includes("EMPTY"),
        hasInvalid: isGeneralOrUnknown
          ? false
          : this.statuses.includes("INVALID"),
        hasOcrWarning: this.statuses.includes("OCR_WARNING"),
      },
    );

    this.batchClassId = batchClassId;
    return this;
  }

  toData(): AcceleratorDocMetadataData & {
    batchInstanceUuid: string;
    batchInstanceId: string;
    source: AcceleratorBatchSource;
  } {
    return {
      uuid: this.uuid,
      batchInstanceId: this.batchInstanceId,
      batchInstanceUuid: this.storeId.batchInstanceUuid,
      classifiedDocType: this.classifiedDocType,
      classifiedModelName: this.classifiedModelName,
      docId: this.storeId.docId,
      pageCount: this.pageCount,
      source: this.source,
      statuses: this.statuses,
    };
  }

  static createTest(
    rest: Partial<
      AcceleratorDocMetadataData & {
        batchInstanceUuid: string;
        batchName: string;
        batchInstanceId: string;
        source: AcceleratorBatchSource;
        batchClassId: string;
      }
    > = {},
  ): AcceleratorDocMetadata {
    const data = {
      uuid: "",
      classifiedDocType: "",
      classifiedModelName: "",
      docId: "DOC1",
      pageCount: 0,
      source: "TRANSACT" as AcceleratorBatchSource,
      statuses: [],
      batchInstanceUuid: "",
      batchInstanceId: "",
      batchClassId: "",
      batchName: "",
      ...rest,
    };

    // batchClassId isn't part of the real constructor's data contract (it's only ever set later,
    // via updateDataFromBatchAndModelInfo() in production), so it has to be assigned after
    // construction here too, the same way AcceleratorBatchDocumentLoader.setDocMetadataFromDoc does.
    const metadata = new AcceleratorDocMetadata(data);
    metadata.batchClassId = data.batchClassId;
    return metadata;
  }

  static createFast(
    docId: string,
    rest: Partial<
      AcceleratorDocMetadataData & {
        batchInstanceUuid: string;
        batchInstanceId: string;
        source: AcceleratorBatchSource;
        batchClassId: string;
      }
    > = {},
  ): AcceleratorDocMetadata {
    return AcceleratorDocMetadata.createTest({
      ...rest,
      docId,
    });
  }
}
